import { useState, useCallback, useEffect, useRef } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Hook for managing human support chat in workflows
 * Handles ticket creation, replies, polling, and state management
 */
export function useHumanSupport({ workflowId, workflowName, enabled = false }) {
    const { user, getUser } = useAuth();

    // State
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const pollRef = useRef(null);
    const hasLoadedRef = useRef(false);

    // API hooks
    const { call: getWorkflowTicket } = useBluelagoon({
        method: "getWorkflowSupportTicket",
    });
    const { call: createTicketApi } = useBluelagoon({
        method: "createTicket",
    });
    const { call: addReplyApi } = useBluelagoon({
        method: "addTicketReply",
    });
    const { call: markTutorialComplete } = useBluelagoon({
        method: "markTutorialComplete",
    });

    // Tutorial and limit state from user
    const hasSeenTutorial = user?.tutorials?.humanSupport?.completed || false;
    const ticketCount =
        user?.tutorials?.humanSupport?.metadata?.ticketCount || 0;

    // Check if user has a support subscription (in supportSubscriptions, not subscriptions)
    const hasSupportSubscription =
        user?.company?.stripeCustomer?.supportSubscriptions?.some(
            (s) => s.status === "ACTIVE"
        );
    const hasReachedLimit = ticketCount >= 2 && !hasSupportSubscription;

    // Format ticket messages for chat display
    const formatMessages = useCallback((ticketMessages) => {
        return ticketMessages.map((msg) => {
            const isStaff =
                msg.isInternal ||
                msg.isFromStaff ||
                msg.sender?.roles?.some(
                    (r) => r === "super-admin" || r === "support"
                );

            return {
                id: msg.id,
                role: isStaff ? "human_support_staff" : "human_support_user",
                content: msg.content,
                createdAt: msg.createdAt,
                files:
                    msg.attachments?.map((a) => ({
                        url: a.url,
                        name: a.name,
                        type: a.type,
                        size: a.size,
                    })) || [],
                senderName: isStaff
                    ? "Support Team"
                    : msg.sender?.firstName || "You",
                ticketMessageId: msg.id,
            };
        });
    }, []);

    // Load existing ticket for this workflow
    const loadTicket = useCallback(async () => {
        if (!workflowId) return null;

        setIsLoading(true);
        try {
            const response = await getWorkflowTicket({ workflowId });
            if (response?.data?.id) {
                setTicket(response.data);
                const formatted = formatMessages(response.data.messages || []);
                setMessages(formatted);
                return { ticket: response.data, messages: formatted };
            } else {
                setTicket(null);
                setMessages([]);
                return null;
            }
        } catch (error) {
            console.error("Error loading support ticket:", error);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [workflowId, getWorkflowTicket, formatMessages]);

    // Send a message (creates ticket or adds reply)
    const sendMessage = useCallback(
        async (content, attachments = []) => {
            if (!content.trim() && attachments.length === 0)
                return { success: false };

            // Block new ticket creation if limit reached (replies are still allowed)
            if (!ticket?.id && hasReachedLimit) {
                toast.error(
                    "You've reached your free support ticket limit. Please upgrade to continue."
                );
                return { success: false, error: "limit_reached" };
            }

            setIsSending(true);
            try {
                const formattedAttachments = attachments
                    .filter((f) => f.url || f.permanentUrl)
                    .map((f) => ({
                        url: f.permanentUrl || f.url,
                        name: f.name || f.fileName || "attachment",
                        type: f.type || f.mimeType,
                        size: f.size,
                    }));

                if (ticket?.id) {
                    // Add reply to existing ticket
                    const response = await addReplyApi({
                        id: ticket.id,
                        message: content,
                        attachments: formattedAttachments,
                    });

                    if (response?.status === 200) {
                        const result = await loadTicket();
                        return {
                            success: true,
                            messages: result?.messages || messages,
                        };
                    } else {
                        throw new Error("Failed to send reply");
                    }
                } else {
                    // Create new ticket
                    const subject = content.split("\n")[0].substring(0, 100);
                    const fullMessage = workflowName
                        ? `**Workflow:** ${workflowName}\n\n---\n\n${content}`
                        : content;

                    const response = await createTicketApi({
                        subject: subject || "Support Request",
                        message: fullMessage,
                        priority: "NORMAL",
                        attachments: formattedAttachments,
                        workflowId,
                    });

                    if (response?.data?.ticketId) {
                        // Update ticket count
                        await markTutorialComplete({
                            tutorialKey: "humanSupport",
                            metadata: { ticketCount: ticketCount + 1 },
                        });
                        getUser?.();

                        const result = await loadTicket();
                        startPolling();
                        return {
                            success: true,
                            messages: result?.messages || [],
                            isNew: true,
                        };
                    } else {
                        throw new Error("Failed to create ticket");
                    }
                }
            } catch (error) {
                console.error("Error sending message:", error);
                toast.error("Failed to send message");
                return { success: false, error };
            } finally {
                setIsSending(false);
            }
        },
        [
            ticket,
            workflowId,
            workflowName,
            ticketCount,
            messages,
            loadTicket,
            addReplyApi,
            createTicketApi,
            markTutorialComplete,
            getUser,
            hasReachedLimit,
        ]
    );

    // Mark tutorial as complete (markTutorialComplete always sets completed: true)
    const completeTutorial = useCallback(async () => {
        try {
            await markTutorialComplete({
                tutorialKey: "humanSupport",
            });
            getUser?.();
        } catch (error) {
            console.error("Error completing tutorial:", error);
        }
    }, [markTutorialComplete, getUser]);

    // Polling for new replies
    const startPolling = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => {
            loadTicket();
        }, 1200000);
    }, [loadTicket]);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    // Load ticket when enabled and workflowId available
    useEffect(() => {
        if (enabled && workflowId && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            loadTicket();
        }
    }, [enabled, workflowId, loadTicket]);

    // Reset when workflowId changes
    useEffect(() => {
        hasLoadedRef.current = false;
        setTicket(null);
        setMessages([]);
    }, [workflowId]);

    // Start/stop polling based on enabled state and ticket existence
    useEffect(() => {
        if (enabled && ticket?.id) {
            startPolling();
        } else {
            stopPolling();
        }
        return () => stopPolling();
    }, [enabled, ticket?.id, startPolling, stopPolling]);

    return {
        // State
        ticket,
        messages,
        isLoading,
        isSending,
        hasTicket: !!ticket?.id,

        // Tutorial/limits
        hasSeenTutorial,
        ticketCount,
        hasReachedLimit,
        hasSupportSubscription,

        // Actions
        sendMessage,
        loadTicket,
        completeTutorial,
        startPolling,
        stopPolling,
    };
}
